import React from "react";
import {
  Table,
  Loader,
  Icon,
  Button,
  Pagination,
  Popup,
} from "semantic-ui-react";
import { UsersV2RoleTag } from "../UsersV2RoleTag";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch {
    return "—";
  }
}

function fullName(user) {
  const parts = [user.firstname, user.lastname].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : "(sin nombre)";
}

/**
 * El backend usa `timestamps: { createdAt: "created_at" }` en el schema, asi
 * que el campo viene en snake_case. Mantenemos un fallback al camelCase por
 * si algun otro endpoint lo expusiera transformado.
 */
function userCreatedAt(u) {
  return u.created_at || u.createdAt;
}

/**
 * Avatar circular con fallback inline a un Icon de Semantic UI cuando no
 * hay URL en BD o cuando la imagen 404. Evitamos depender de un PNG en
 * /public que podria no existir.
 */
function AvatarCell({ src }) {
  const [failed, setFailed] = React.useState(false);
  if (!src || failed) {
    return (
      <Icon
        name="user circle"
        size="big"
        style={{ color: "#bdbdbd", margin: 0 }}
      />
    );
  }
  return (
    <img
      src={src}
      alt=""
      onError={() => setFailed(true)}
      style={{
        width: 32,
        height: 32,
        minWidth: 32,
        maxWidth: 32,
        flexShrink: 0,
        borderRadius: "50%",
        objectFit: "cover",
        display: "block",
      }}
    />
  );
}

/**
 * Tabla densa del listado v2. Lectura pura — la fila completa abre el drawer
 * 360 (`onSelect`) y el icono de papelera dispara el modal de borrado
 * (`onDelete`).
 */
export function UsersV2Table({
  data,
  loading,
  page,
  totalPages,
  onPageChange,
  onSelect,
  onDelete,
}) {
  if (loading && (!data || data.length === 0)) {
    return (
      <div style={{ padding: 32 }}>
        <Loader active inline="centered">
          Cargando usuarios...
        </Loader>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div style={{ padding: 32, textAlign: "center", color: "#666" }}>
        <Icon name="search" size="big" />
        <p>No hay usuarios que coincidan con los filtros.</p>
      </div>
    );
  }

  return (
    <>
      <Table compact="very" celled selectable striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell collapsing> </Table.HeaderCell>
            <Table.HeaderCell>Email</Table.HeaderCell>
            <Table.HeaderCell>Nombre</Table.HeaderCell>
            <Table.HeaderCell>Roles</Table.HeaderCell>
            <Table.HeaderCell collapsing>Estado</Table.HeaderCell>
            <Table.HeaderCell collapsing>Creado</Table.HeaderCell>
            <Table.HeaderCell collapsing>Avisos</Table.HeaderCell>
            <Table.HeaderCell collapsing textAlign="center">
              Acciones
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {data.map((user) => (
            <Table.Row
              key={user._id}
              style={{ cursor: "pointer" }}
              onClick={() => onSelect && onSelect(user)}
            >
              <Table.Cell collapsing textAlign="center">
                <AvatarCell src={user.avatar} />
              </Table.Cell>
              <Table.Cell>
                <strong style={{ color: "#1f6feb" }}>
                  {user.email || "(sin email)"}
                </strong>
              </Table.Cell>
              <Table.Cell>{fullName(user)}</Table.Cell>
              <Table.Cell>
                {Array.isArray(user.roles) && user.roles.length > 0 ? (
                  user.roles.map((r) => <UsersV2RoleTag key={r} role={r} />)
                ) : (
                  <span style={{ color: "#aaa" }}>—</span>
                )}
              </Table.Cell>
              <Table.Cell collapsing>
                {user.active ? (
                  <span style={{ color: "#21ba45", whiteSpace: "nowrap" }}>
                    <Icon name="check circle" /> Activo
                  </span>
                ) : (
                  <span style={{ color: "#999", whiteSpace: "nowrap" }}>
                    <Icon name="ban" /> Inactivo
                  </span>
                )}
              </Table.Cell>
              <Table.Cell collapsing>
                {formatDate(userCreatedAt(user))}
              </Table.Cell>
              <Table.Cell collapsing textAlign="center">
                {user.isOrphan && (
                  <Popup
                    content="Usuario huerfano: su rol implica datos populados que no existen."
                    trigger={
                      <Icon name="warning sign" color="orange" size="large" />
                    }
                  />
                )}
              </Table.Cell>
              <Table.Cell textAlign="center" collapsing>
                <Button.Group size="mini">
                  <Popup
                    content="Ver detalle 360"
                    trigger={
                      <Button
                        icon="eye"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onSelect) onSelect(user);
                        }}
                      />
                    }
                  />
                  <Popup
                    content="Borrar / desactivar"
                    trigger={
                      <Button
                        icon="trash"
                        color="red"
                        basic
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onDelete) onDelete(user);
                        }}
                      />
                    }
                  />
                </Button.Group>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "1em",
          }}
        >
          <Pagination
            activePage={page}
            totalPages={totalPages}
            siblingRange={2}
            onPageChange={(_, { activePage }) => onPageChange(activePage)}
          />
        </div>
      )}
    </>
  );
}
